import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { translations, LANGS } from "./translations";

const I18nContext = createContext(null);

function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const lang = (navigator.language || "en").toLowerCase();
  if (lang.startsWith("ro")) return "ro";
  return "en";
}

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const urlLang = LANGS.includes(params.lang) ? params.lang : null;

  const [lang, setLangState] = useState(() => {
    if (urlLang) return urlLang;
    const stored = localStorage.getItem("lp_lang");
    if (stored && LANGS.includes(stored)) return stored;
    return detectBrowserLang();
  });

  useEffect(() => {
    if (urlLang && urlLang !== lang) setLangState(urlLang);
    // eslint-disable-next-line
  }, [urlLang]);

  useEffect(() => {
    localStorage.setItem("lp_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next) => {
    if (!LANGS.includes(next)) return;
    setLangState(next);
    // swap URL prefix
    const segs = location.pathname.split("/").filter(Boolean);
    if (LANGS.includes(segs[0])) segs[0] = next;
    else segs.unshift(next);
    navigate("/" + segs.join("/") + location.search, { replace: false });
  };

  const t = useMemo(() => translations[lang], [lang]);

  const value = useMemo(() => ({ lang, setLang, t, langs: LANGS }), [lang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

/** prefix a path with the current language */
export function useLocalizedPath() {
  const { lang } = useI18n();
  return (path) => {
    const clean = path.startsWith("/") ? path : `/${path}`;
    return `/${lang}${clean === "/" ? "" : clean}`;
  };
}
